/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path context
 * @description Application context provider
 */
import React from "react";
import { useTranslation } from "react-i18next";
import * as config from "./../configuration/index";
import { AppContext, useAppContextHook } from "./AppContext";
import { AppLevelContext } from "./AppCtx";
/**
 * This is a workaround for getting the current global context value for any function
 * without actually accessing the context.
 */
let currentGlobalContextValue: any = {};
export const getCurrentGlobalContext = (): AppLevelContext =>
  currentGlobalContextValue;
export const AppCtxProvider: React.FC<{
  children: React.ReactNode;
}> = (props: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  const [ctxValue, setCtxValue] = React.useState(() => ({
    editMode: false,
    appBarVisible: true,
    language: "en",
    rtl: i18n.dir() === "rtl",
    originalPeriod: "",
    period: config.System.settings.defaultPeriod,
    modelId: config.System.settings.defaultModel,
    fullModelId: `SC.${config.System.settings.defaultModel}`,
    networkErrors: [],
    pageAnchors: [],
    serverURL: process.env.REACT_APP_SERVER_URL,
    sendValue: <K extends keyof AppLevelContext>(
      name: K,
      value: AppLevelContext[K]
    ) => {
      if (name === "model") {
        if (!(typeof value === "string")) {
          return;
        }
      }
      if (name === "period" && typeof value === "string") {
        setCtxValue((oldState) => {
          return {
            ...oldState,
            originalPeriod: value,
            [name]: value,
          };
        });
      } else {
        setCtxValue((oldState) => {
          return {
            ...oldState,
            [name]: value,
          };
        });
      }
    },
    lastSearchUpdateTime: Date.now(),
  }));
  /**
   * Short circuit the context value to a global variable
   */
  currentGlobalContextValue = ctxValue;
  return (
    <AppContext.Provider value={ctxValue}>
      {props?.children}
    </AppContext.Provider>
  );
};
export const useAppContext = useAppContextHook;
